// SlotOverrideModal.js - Lägg till eller ändra en schemaslot för EN dag
//
// Skapar en per-datum-avvikelse (override) via högerklick i kalendern:
// - mode 'add':  ny slot bara denna dag
// - mode 'edit': ändra en befintlig slot denna dag (mall-slot -> 'modify',
//                add-override-slot -> ta bort gamla + skapa ny)
// Mallen påverkas aldrig.

import React, { useState } from 'react';
import { createSlotOverride, deleteSlotOverride } from './calendarApi';
import './SlotOverrideModal.css';

const WEEKDAYS = ['måndag', 'tisdag', 'onsdag', 'torsdag', 'fredag', 'lördag', 'söndag'];

// 'HH:MM' + minuter -> 'HH:MM'
const addMinutes = (time, mins) => {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m + mins;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

const SlotOverrideModal = ({ mode, date, userId, slot, appointmentTypes, defaultStartTime, onClose, onSaved }) => {
  // date: Date-objekt för dagen. slot: befintlig slot (vid edit), annars null.
  // defaultStartTime: 'HH:MM' från den klickade tiden (Spår C), används vid 'add'.
  const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  const activeTypes = (appointmentTypes || []).filter(t => t.active !== false);

  const defaultType = slot?.appointment_type_id || activeTypes[0]?.type_id || '';
  const defaultTypeObj = activeTypes.find(t => t.type_id === defaultType);

  // Vid edit styr slotens egen tid; vid add används klickad tid, annars 09:00.
  const initialStart = slot?.start_time || defaultStartTime || '09:00';

  const [typeId, setTypeId] = useState(defaultType);
  const [startTime, setStartTime] = useState(initialStart);
  const [endTime, setEndTime] = useState(
    slot?.end_time || addMinutes(initialStart, defaultTypeObj?.duration_minutes || 30)
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Vid typbyte: justera sluttid efter typens standardlängd (om användaren inte
  // redan ändrat manuellt känns detta naturligt)
  const handleTypeChange = (newId) => {
    setTypeId(newId);
    const t = activeTypes.find(x => x.type_id === parseInt(newId));
    if (t) setEndTime(addMinutes(startTime, t.duration_minutes));
  };

  const handleSave = async () => {
    if (!typeId) { setError('Välj en bokningstyp'); return; }
    if (startTime >= endTime) { setError('Starttiden måste vara före sluttiden'); return; }
    try {
      setSaving(true);
      setError(null);

      if (mode === 'add') {
        await createSlotOverride({
          user_id: userId, date: dateStr, action: 'add',
          start_time: startTime, end_time: endTime, appointment_type_id: parseInt(typeId)
        });
      } else if (slot?.slot_id) {
        // Mall-genererad slot -> modifiera denna dag
        await createSlotOverride({
          user_id: userId, date: dateStr, action: 'modify', source_slot_id: slot.slot_id,
          start_time: startTime, end_time: endTime, appointment_type_id: parseInt(typeId)
        });
      } else if (slot?.override_id) {
        // Redan en add-avvikelse -> ersätt (ta bort + skapa ny)
        await deleteSlotOverride(slot.override_id);
        await createSlotOverride({
          user_id: userId, date: dateStr, action: 'add',
          start_time: startTime, end_time: endTime, appointment_type_id: parseInt(typeId)
        });
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.msg || 'Kunde inte spara schemasloten');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="som-overlay" onClick={onClose}>
      <div className="som-modal" onClick={e => e.stopPropagation()}>
        <div className="som-header">
          <h3>{mode === 'add' ? 'Lägg schemaslot' : 'Ändra schemaslot'}</h3>
          <button className="som-close" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>

        <p className="som-date">
          {WEEKDAYS[(date.getDay() + 6) % 7]} {dateStr}
          <span className="som-date-hint">Gäller endast denna dag</span>
        </p>

        {error && <div className="som-error">{error}</div>}

        <div className="som-field">
          <label>Bokningstyp</label>
          <select value={typeId} onChange={e => handleTypeChange(e.target.value)}>
            <option value="">Välj typ...</option>
            {activeTypes.map(t => (
              <option key={t.type_id} value={t.type_id}>{t.name} ({t.duration_minutes} min)</option>
            ))}
          </select>
        </div>

        <div className="som-times">
          <div className="som-field">
            <label>Från</label>
            <input type="time" step="900" value={startTime} onChange={e => setStartTime(e.target.value)} />
          </div>
          <div className="som-field">
            <label>Till</label>
            <input type="time" step="900" value={endTime} onChange={e => setEndTime(e.target.value)} />
          </div>
        </div>

        <div className="som-actions">
          <button className="btn btn-secondary" onClick={onClose}>Avbryt</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Sparar...' : 'Spara'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SlotOverrideModal;
